import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import { attemptLogin } from "../../services/auth/auth.service";
import { fetchProfileData, getUserList } from "../../services/workspace/user.service";
import { convertToLabelValue } from "../../utils/common";

// -------------------- PERMISSION TRANSFORMATION --------------------

/**
 * Transform permissions from API format to Redux format
 * API format: { "basic-setup": { "role": { "1": "role-create" } } }
 * Redux format: { "basic-setup": { "role": ["role-create"] } }
 */
const transformPermissionsFormat = (permissions) => {
  if (!permissions) return null;
  
  // If already in correct format (array-based), return as-is
  if (Array.isArray(permissions)) {
    return permissions;
  }
  
  // Transform object-based permissions to array-based format
  const transformed = {};
  
  Object.keys(permissions).forEach(module => {
    const moduleData = permissions[module];
    if (typeof moduleData === 'object' && moduleData !== null) {
      transformed[module] = {};
      
      Object.keys(moduleData).forEach(submodule => {
        const submoduleData = moduleData[submodule];
        
        if (Array.isArray(submoduleData)) {
          // Already an array, use as-is
          transformed[module][submodule] = submoduleData;
        } else if (typeof submoduleData === 'object' && submoduleData !== null) {
          // Convert object values to array
          const permissionArray = Object.values(submoduleData).filter(perm => typeof perm === 'string');
          transformed[module][submodule] = permissionArray;
        } else if (typeof submoduleData === 'string') {
          // Single permission string
          transformed[module][submodule] = [submoduleData];
        }
      });
    }
  });
  
  return transformed;
};

// -------------------- Initial State --------------------
export const initialState = {
  // ===== CORE AUTHENTICATION STATE =====
  userInfo: null,           // Complete user information object
  isAuthenticated: false,   // Authentication status flag
  user: null,              // Current user data
  role: null,              // User role/type
  permissions: null,       // User permissions array
  ws_type: null,           // Workspace type
  
  // ===== LOADING & ERROR STATES =====
  status: "idle",          // Async operation status (idle/loading/succeeded/failed)
  loading: true,           // General loading state (starts as true to wait for rehydration)
  error: null,             // Error messages
  
  // ===== PROFILE MANAGEMENT =====
  profileLoad: false,      // Profile loading state
  
  // ===== STAFF MANAGEMENT =====
  staffList: [],           // List of staff members
  staffLoading: false,     // Staff list loading state
  staffError: null,        // Staff list error state
  
  // ===== DASHBOARD DATA =====
  userDashboard: null,     // User dashboard data
  dashboardLoad: true,     // Dashboard loading state
  
  // ===== NOTIFICATIONS & REMINDERS =====
  reminders: [],           // User reminders list
  circularList: [],        // Circular notifications list
  unreadCirculars: [],     // Unread circular notifications
  
  // ===== ATTENDANCE & LEAVE =====
  activeAttendance: [],    // Active attendance records
  leaveRefresh: false,     // Leave data refresh flag
};

// -------------------- Async Thunks --------------------

// login
export const loginUser = createAsyncThunk(
  "user/loginUser",
  async (userCredentials, { rejectWithValue, dispatch }) => {
    try {
      const response = await attemptLogin({...userCredentials,ws_type:2});

      if (response.data.data && (!response.data.data.user || Object.keys(response.data.data.user).length === 0)) {
        try {
          const profileResponse = await fetchProfileData();
          if (profileResponse.data?.data) {
            response.data.data.user = profileResponse.data.data;
          }
        } catch (profileError) {
          console.warn("Failed to fetch user profile after login:", profileError);
        }
      }

      return response.data;
    } catch (err) {
      let errorMessage = "Login failed. Please try again.";
      if (err.response?.data?.message) errorMessage = err.response.data.message;
      else if (err.response?.data?.error) errorMessage = err.response.data.error;
      else if (err.message) errorMessage = err.message;

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * FETCH USER PROFILE
 * ==================
 * Retrieves current user's profile information
 * - Fetches complete user profile data
 * - Updates user state with fresh profile information
 * - Also updates permissions, workspace data if included in profile response
 * - Matches office project implementation
 */
export const fetchUserProfile = createAsyncThunk(
  "user/fetchUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchProfileData();
      // Profile API response structure - return complete response data
      // Office format: response.data.user
      // hr-frontend format: response.data.data or response.data.user
      // Return complete response data so we can store everything
      return response.data || null;
    } catch (err) {
      // Handle different error formats
      let errorMessage = "Failed to fetch user profile.";
      if (err.response?.data?.message) errorMessage = err.response.data.message;
      else if (err.message) errorMessage = err.message;

      return rejectWithValue(errorMessage);
    }
  }
);

// 👇 NEW: fetch staff list
export const fetchStaffList = createAsyncThunk(
  "user/fetchStaffList",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getUserList(params);
      return response?.data || response; // adjust depending on API
    } catch (err) {
      let errorMessage = "Failed to fetch staff list.";
      if (err.response?.data?.message) errorMessage = err.response.data.message;
      else if (err.message) errorMessage = err.message;

      return rejectWithValue(errorMessage);
    }
  }
);

// -------------------- Slice --------------------
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    // ===== ATTENDANCE & LEAVE MANAGEMENT =====
    
    /**
     * Set active attendance records
     * @param {Object} state - Current state
     * @param {Array} action.payload - Array of active attendance records
     */
    _setActiveAttendance: (state, action) => {
      state.activeAttendance = action.payload;
    },
    
    /**
     * Set leave refresh flag
     * @param {Object} state - Current state
     * @param {boolean} action.payload - Leave refresh flag
     */
    _setLeaveRefresh: (state, action) => {
      state.leaveRefresh = action.payload || false;
    },

    // ===== REMINDERS MANAGEMENT =====
    
    /**
     * Add new reminder to the list
     * @param {Object} state - Current state
     * @param {Object} action.payload - Reminder object to add
     */
    _setReminder: (state, action) => {
      state.reminders.push(action.payload);
    },
    
    /**
     * Remove reminder from the list
     * @param {Object} state - Current state
     * @param {Object} action.payload - Reminder object with id to remove
     */
    _removeReminder: (state, action) => {
      const updatedItems = state.reminders?.filter((item) => item?.id !== action.payload?.id);
      state.reminders = updatedItems;
    },

    // ===== USER DATA MANAGEMENT =====
    
    /**
     * Set user data in userInfo
     * @param {Object} state - Current state
     * @param {Object} action.payload - User data object
     */
    _setUserData: (state, action) => {
      state.userInfo = { ...state.userInfo, user: action.payload };
    },
    
    /**
     * Update workspace data in userInfo
     * @param {Object} state - Current state
     * @param {Object} action.payload - Workspace data object (logo, name, isWhiteLabel, etc.)
     */
    _updateWorkspace: (state, action) => {
      if (state.userInfo) {
        state.userInfo = {
          ...state.userInfo,
          workspace: {
            ...(state.userInfo.workspace || {}),
            ...action.payload,
          },
        };
        // Also update workspace in user object if it exists
        if (state.userInfo.user) {
          state.userInfo.user = {
            ...state.userInfo.user,
            workspace: {
              ...(state.userInfo.user.workspace || {}),
              ...action.payload,
            },
          };
        }
      } else {
        // Initialize userInfo if it doesn't exist
        state.userInfo = {
          workspace: {
            ...action.payload,
          },
        };
      }
    },
    
    /**
     * Update user data with onboarding completion
     * @param {Object} state - Current state
     * @param {Object} action.payload - Update payload (currently unused)
     */
    _updateUserData: (state, action) => { 
      state.userInfo = {
        ...state.userInfo,
        user: {
          ...state.userInfo?.user,
          onboarding: 1,
        },
      };
    },
    
    /**
     * Set profile loading state
     * @param {Object} state - Current state
     * @param {boolean} action.payload - Loading state
     */
    _setProfileLoad: (state, action) => {
      state.profileLoad = action.payload;
    },

    // ===== CIRCULAR NOTIFICATIONS =====
    
    /**
     * Set complete circular list
     * @param {Object} state - Current state
     * @param {Array} action.payload - Array of circular notifications
     */
    _setCircularList: (state, action) => {
      state.circularList = action.payload;
    },
    
    /**
     * Add new circular to the list and mark as unread
     * @param {Object} state - Current state
     * @param {Object} action.payload - New circular object
     */
    _updateCircularList: (state, action) => {
      state.circularList.unshift(action.payload);
      state.unreadCirculars.push(action.payload);
    },
    
    /**
     * Mark circular as read (remove from unread list)
     * @param {Object} state - Current state
     * @param {Object} action.payload - Circular object with id to mark as read
     */
    _updateUnreadCirculars: (state, action) => {
      const updatedItems = state.unreadCirculars?.filter((item) => item?.id !== action.payload?.id);
      state.unreadCirculars = updatedItems;
    },

    // ===== DASHBOARD MANAGEMENT =====
    
    /**
     * Set user dashboard data
     * @param {Object} state - Current state
     * @param {Object} action.payload - Dashboard data object
     */
    _setUserDashboard: (state, action) => {
      state.userDashboard = action.payload;
      state.dashboardLoad = false;
    },

    // ===== STAFF MANAGEMENT =====
    
    /**
     * Reset staff list and clear errors
     * @param {Object} state - Current state
     */
    _resetStaffList: (state) => {
      state.staffList = [];
      state.staffError = null;
    },

    // ===== AUTHENTICATION STATE MANAGEMENT =====
    
    /**
     * Set authentication status
     * @param {Object} state - Current state
     * @param {boolean} action.payload - Authentication status
     */
    _setAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload;
    },
    
    /**
     * Logout user and reset all authentication-related state
     * @param {Object} state - Current state
     */
    _logoutUser: (state) => {
      state.userInfo = null;
      state.isAuthenticated = false;
      state.user = null;
      state.role = null;
      state.permissions = null;
      state.ws_type = null;
      state.status = "idle";
      state.error = null;
      state.loading = false;
      state.profileLoad = false; // Ensure profileLoad is also false
    },
    
    /**
     * Reset login state (clear errors and loading)
     * @param {Object} state - Current state
     */
    _resetLoginState: (state) => {
      state.status = "idle";
      state.error = null;
      state.loading = false;
    },
    
    /**
     * Sync permissions to Redux store
     * Used when updating user permissions to keep Redux state in sync
     * Only applies to users who are not type 1 (workspace admin)
     * @param {Object} state - Current state
     * @param {Array|Object} action.payload - Updated permissions (array or object)
     */
    _syncPermissions: (state, action) => {
      // Only sync permissions if user is not type 1 (workspace admin)
      if (state.userInfo?.user?.type !== 1) {
        // Transform permissions to the correct format expected by menu filtering
        const transformedPermissions = transformPermissionsFormat(action.payload);
        state.permissions = transformedPermissions;
        // Also update permissions in userInfo if it exists
        if (state.userInfo) {
          state.userInfo.permissions = transformedPermissions;
        }
      }
    },
  },
  // ===== ASYNC THUNK HANDLERS =====
  extraReducers: (builder) => {
    builder
      // ===== REHYDRATE HANDLER =====
      // Handle redux-persist rehydration
      // Note: persistReducer automatically merges persisted state before this handler runs
      // So we just need to set loading to false after rehydration completes
      .addCase(REHYDRATE, (state) => {
        // Rehydration is complete, set loading to false
        // The persisted state (userInfo, isAuthenticated, etc.) is already merged by persistReducer
        state.loading = false;
      })
      // ===== LOGIN USER HANDLERS =====
      .addCase(loginUser.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null; // Clear previous errors
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.userInfo = action.payload;
        state.isAuthenticated = true;
        state.error = null;
        state.loading = false;

        // Handle both API response shapes:
        //   { data: { user, permissions, ws_type }, token }  ← login API
        //   { user, permissions, ws_type, token }            ← some variants
        const responseData = action?.payload?.data || action?.payload;
        const userData = responseData?.user || null;

        state.user = userData;
        state.role = userData?.type ?? null;
        state.ws_type = responseData?.ws_type || null;
        // Transform permissions to array format so menu filtering works on first login
        state.permissions = transformPermissionsFormat(responseData?.permissions) || null;

        // Save token to localStorage
        const hrToken = action.payload?.token || responseData?.token;
        if (hrToken) {
          localStorage.setItem('user', JSON.stringify({ token: hrToken }));
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.loading = false;
        state.isAuthenticated = false; // Ensure auth is false on failure
      })

      // ===== FETCH USER PROFILE HANDLERS =====
      .addCase(fetchUserProfile.pending, (state) => {
        state.profileLoad = true;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.profileLoad = false;
        if (action.payload) {
          
          // Extract user data - profile API returns: { message: "...", user: {...} }
          const userData = action.payload.user || action.payload.data?.user || action.payload.data || action.payload;
          
          // Update userInfo with fresh profile data
          if (state.userInfo) {
            state.userInfo.user = userData;
            // Update other fields if present in response
            if (action.payload.permissions !== undefined) {
              state.userInfo.permissions = action.payload.permissions;
            }
            if (action.payload.ws_type !== undefined) {
              state.userInfo.ws_type = action.payload.ws_type;
            }
            if (action.payload.workspace !== undefined) {
              state.userInfo.workspace = action.payload.workspace;
            }
          } else {
            // Initialize userInfo if it doesn't exist
            state.userInfo = {
              user: userData,
              permissions: action.payload.permissions,
              ws_type: action.payload.ws_type,
              workspace: action.payload.workspace,
            };
          }
          
          // Also update the standalone user state
          state.user = userData;
          
          // Update permissions if provided in profile response
          if (action.payload.permissions !== undefined) {
            state.permissions = action.payload.permissions;
          }
          
          // Update workspace type if provided
          if (action.payload.ws_type !== undefined) {
            state.ws_type = action.payload.ws_type;
          }
          
          // Update role if provided in user data
          if (userData?.type) {
            state.role = userData.type;
          }
        }
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.profileLoad = false;
        console.error("Failed to fetch user profile:", action.payload);
      })

      // ===== FETCH STAFF LIST HANDLERS =====
      .addCase(fetchStaffList.pending, (state) => {
        state.staffLoading = true;
        state.staffError = null; // Clear previous errors
      })
      .addCase(fetchStaffList.fulfilled, (state, action) => {
        state.staffLoading = false;
        // Extract array from response: response.data.user.data or response.data.data or response.data
        const userData = action.payload?.user?.data || action.payload?.data || action.payload;
        // Ensure it's an array and convert to label/value format
        const staffArray = Array.isArray(userData) ? userData : [];
        state.staffList = convertToLabelValue(staffArray);
      })
      .addCase(fetchStaffList.rejected, (state, action) => {
        state.staffLoading = false;
        state.staffError = action.payload;
        state.staffList = []; // Reset to empty array on error
      });
  },
});

// -------------------- Exports --------------------

/**
 * ACTION CREATORS EXPORT
 * =======================
 * All action creators are exported for use in components
 * Organized by functionality for better maintainability
 */
export const {
  // ===== ATTENDANCE & LEAVE ACTIONS =====
  _setActiveAttendance,
  _setLeaveRefresh,
  
  // ===== USER DATA ACTIONS =====
  _setUserData,
  _updateUserData,
  _updateWorkspace,
  _setProfileLoad,
  
  // ===== REMINDERS ACTIONS =====
  _setReminder,
  _removeReminder,
  
  // ===== CIRCULAR NOTIFICATIONS ACTIONS =====
  _setCircularList,
  _updateCircularList,
  _updateUnreadCirculars,
  
  // ===== DASHBOARD ACTIONS =====
  _setUserDashboard,
  
  // ===== STAFF MANAGEMENT ACTIONS =====
  _resetStaffList,
  
  // ===== AUTHENTICATION ACTIONS =====
  _setAuthenticated,
  _logoutUser,
  _resetLoginState,
  
  // ===== PERMISSIONS SYNC ACTIONS =====
  _syncPermissions,
} = userSlice.actions;

/**
 * REDUCER EXPORT
 * ==============
 * Default export of the user slice reducer
 * This is what gets added to the store configuration
 */
export default userSlice.reducer;
